import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import {
  CurrentScopeFacade,
  EmptyStateComponent,
  InformationBoxComponent,
  PracticeSelectorComponent,
  StatusLabelComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  DateRangeFormComponent,
  FileManagerService,
  type IMediaPath,
  NgCommonModule,
  TrackByFunctions,
  TypedFormControl,
  formControlChanges$,
} from '@principle-theorem/ng-shared';
import {
  Brand,
  Staffer,
  staffToNamedDocs,
} from '@principle-theorem/principle-core';
import {
  type IBrand,
  IPractice,
  type IPractitionerFTAReportParams,
  type IPractitionerGapsJobParams,
  type IReportingJob,
  IReportingJobDisplay,
  type IStaffer,
  REPORTING_JOBS_DISPLAY,
  type ReportingJob,
  ReportingJobFileType,
  ReportingJobStatus,
  ReportingJobType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_FORMAT,
  type INamedDocument,
  type ITimePeriod,
  type WithRef,
  addDoc,
  filterUndefined,
  multiSort,
  snapshot,
  sortTimestamp,
  toTimestampRange,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import * as moment from 'moment-timezone';
import { type Observable, ReplaySubject, Subject } from 'rxjs';
import { map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { SystemReportParameterFormComponent } from '../system-report-parameter-form/system-report-parameter-form.component';

export const PUBLIC_REPORTS = [
  ReportingJobType.AppointmentsWithoutClinicalNote,
  ReportingJobType.OnlineBookings,
];

export const REPORTING_JOB_STATUS_MAP: {
  [key in ReportingJobStatus]: string;
} = {
  [ReportingJobStatus.Pending]: 'default',
  [ReportingJobStatus.InProgress]: 'accent',
  [ReportingJobStatus.Complete]: 'primary',
  [ReportingJobStatus.Failed]: 'warn',
};

@Component({
  selector: 'pr-system-reports',
  templateUrl: './system-reports.component.html',
  styleUrl: './system-reports.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormFieldModule,
    MatTooltip,
    MatSelectModule,
    MatButton,
    MatListModule,
    NgCommonModule,
    EmptyStateComponent,
    StatusLabelComponent,
    DateRangeFormComponent,
    InformationBoxComponent,
    SystemReportParameterFormComponent,
    PracticeSelectorComponent,
  ],
})
export class SystemReportsComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  trackByReportName = TrackByFunctions.variable<ReportingJobType>();
  reportCtrl = new TypedFormControl<IReportingJobDisplay>();
  fileType = new TypedFormControl<ReportingJobFileType>(
    ReportingJobFileType.Csv
  );
  from: moment.Moment = moment().startOf('day').subtract({ weeks: 1 });
  to: moment.Moment = moment().endOf('day');
  dateFormat = DATE_FORMAT;
  statusColourMap = REPORTING_JOB_STATUS_MAP;
  brand$: Observable<WithRef<IBrand>>;
  practice$ = new ReplaySubject<WithRef<IPractice>>(1);
  availableSystemReports$: Observable<IReportingJobDisplay[]>;
  existingJobs$: Observable<WithRef<IReportingJob>[]>;
  filteredReportJobs$: Observable<WithRef<IReportingJob>[]>;
  practitioners$: Observable<INamedDocument<IStaffer>[]>;
  reportParams$ = new ReplaySubject<
    IPractitionerFTAReportParams | IPractitionerGapsJobParams | undefined
  >(1);
  practiceScopedReports = [
    ReportingJobType.AccountCreditsUsedUpUntilDate,
    ReportingJobType.CreditNotesByPractice,
  ];
  practiceScopedEnabled$: Observable<boolean>;

  constructor(
    scope: CurrentScopeFacade,
    private _fileManager: FileManagerService
  ) {
    this.brand$ = scope.currentBrand$.pipe(filterUndefined());
    this.availableSystemReports$ = this.brand$.pipe(
      map((brand) => {
        const brandReports = brand.settings.availableSystemReports?.length
          ? brand.settings.availableSystemReports
          : [];

        const options = [...PUBLIC_REPORTS, ...brandReports];
        return sortBy(
          REPORTING_JOBS_DISPLAY.filter((jobDisplay) =>
            options.includes(jobDisplay.type)
          ),
          'name'
        );
      })
    );

    this.availableSystemReports$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((reports) => {
        if (reports.length) {
          this.reportCtrl.setValue(reports[0]);
        }
      });

    this.existingJobs$ = this.brand$.pipe(
      switchMap((brand) => Brand.reportingJobs$(brand))
    );

    this.practitioners$ = this.brand$.pipe(
      switchMap((brand) => Staffer.practitionersByBrand$(brand)),
      staffToNamedDocs()
    );

    this.filteredReportJobs$ = formControlChanges$(this.reportCtrl).pipe(
      switchMap((reportingJob) =>
        this.existingJobs$.pipe(
          map((jobs) =>
            jobs.filter((job) =>
              reportingJob ? job.type === reportingJob.type : true
            )
          )
        )
      ),
      multiSort((reportA, reportB) =>
        sortTimestamp(reportA.createdAt, reportB.createdAt)
      )
    );

    scope.currentPractice$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((practice) => this.practice$.next(practice));

    this.practiceScopedEnabled$ = this.reportCtrl.valueChanges.pipe(
      map((report) => this.practiceScopedReports.includes(report.type)),
      startWith(false)
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  updateParams(
    change: IPractitionerFTAReportParams | IPractitionerGapsJobParams
  ): void {
    this.reportParams$.next(change);
  }

  transformReportName(report: ReportingJobType): string {
    return report.split('.')[1].split('-').join(' ');
  }

  updateDateRange(dateRange: ITimePeriod): void {
    this.from = dateRange.from;
    this.to = dateRange.to;
  }

  getTooltip(job: WithRef<IReportingJob>): string {
    switch (job.status) {
      case ReportingJobStatus.InProgress:
        return 'Report in progress';
      case ReportingJobStatus.Failed:
        return 'Report failed';
      default:
        return '';
    }
  }

  isNotReadyForDownload(job: WithRef<IReportingJob>): boolean {
    return job.status !== ReportingJobStatus.Complete || !job.file;
  }

  async runJob(): Promise<void> {
    const brand = await snapshot(this.brand$);
    const practice = await snapshot(this.practice$);
    const params = await snapshot(this.reportParams$);
    const jobRequest: ReportingJob = {
      type: this.reportCtrl.value.type,
      requestParams: {
        range: {
          ...toTimestampRange({ from: this.from, to: this.to }),
        },
        brandRef: brand.ref,
        practiceRef: practice.ref,
        fileType: this.fileType.value,
        ...params,
      },
      status: ReportingJobStatus.Pending,
      deleted: false,
    };

    await addDoc(Brand.reportingJobCol(brand), jobRequest);
  }

  async download(job: WithRef<IReportingJob>): Promise<void> {
    if (!job.file) {
      return;
    }

    await this._fileManager.downloadFile(job.file as IMediaPath);
  }
}
